import {motion, useReducedMotion} from 'motion/react';
import {Button} from '@/components/ui/button';
import {Floating} from '@/components/motion/Floating';
import {Counter} from '@/components/motion/Counter';
import {asset} from '@/lib/asset';
import {CTA_LABEL} from '@/lib/registration-window';
import {config} from '@/config';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Puntos de luz ámbar tipo guirnalda alrededor del bodegón. */
function Lights() {
  const reduced = useReducedMotion();
  const points = [
    {top: '8%', left: '30%'},
    {top: '4%', left: '55%'},
    {top: '14%', left: '78%'},
    {top: '38%', left: '92%'},
    {top: '66%', left: '86%'},
    {top: '88%', left: '62%'},
    {top: '84%', left: '28%'},
    {top: '58%', left: '6%'},
    {top: '28%', left: '10%'},
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {points.map((p, i) => (
        <motion.span
          key={i}
          className="absolute h-2 w-2 rounded-full bg-cream-bright shadow-[0_1px_12px_2px_rgba(237,171,67,0.9)]"
          style={p}
          animate={reduced ? undefined : {opacity: [0.5, 1, 0.5], scale: [0.85, 1.1, 0.85]}}
          transition={{duration: 2.4, delay: i * 0.28, repeat: Infinity, ease: 'easeInOut'}}
        />
      ))}
    </div>
  );
}

export function Hero({onVendorCta, onExplore}: {onVendorCta: () => void; onExplore: () => void}) {
  const reduced = useReducedMotion();

  const stagger = (i: number) => ({
    initial: reduced ? false : {opacity: 0, y: 28},
    animate: {opacity: 1, y: 0},
    transition: {duration: 0.8, delay: 0.15 + i * 0.12, ease: EASE},
  });

  return (
    <section id="inicio" className="relative overflow-hidden" aria-label="Navimarket 2026">
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-[1.05fr_1fr] lg:gap-4 lg:pb-24 lg:pt-16">
        {/* Columna de mensaje */}
        <div className="relative z-10 max-w-xl">
          <motion.p {...stagger(0)} className="overline-label text-amber">
            {config.EVENT.dateShort} · {config.EVENT.city}
          </motion.p>

          <motion.div {...stagger(1)} className="mt-5">
            <h1 className="sr-only">Navimarket 2026 — ATH Móvil</h1>
            <img
              src={asset('img/logos/navimarket-stack.webp')}
              alt=""
              aria-hidden
              className="w-64 sm:w-80"
              fetchPriority="high"
            />
          </motion.div>

          <motion.h2
            {...stagger(2)}
            className="mt-7 text-balance text-3xl font-bold uppercase leading-[1.08] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]"
          >
            La Navidad empieza a moverse con <span className="text-amber">ATH Móvil</span> y{' '}
            <span className="text-tangerine">tu negocio</span>.
          </motion.h2>

          <motion.p
            {...stagger(3)}
            className="mt-5 max-w-md text-pretty text-base leading-relaxed text-cream/90 sm:text-lg"
          >
            Este domingo, 15 de noviembre, el Navimarket abre la temporada navideña con el bazar más grande de Puerto
            Rico.
          </motion.p>

          <motion.div {...stagger(4)} className="mt-8 flex flex-wrap items-center gap-4">
            <Button
              size="lg"
              onClick={onVendorCta}
              className="h-12 rounded-full bg-tangerine px-8 text-base font-medium text-white shadow-[0_8px_30px_-6px_rgba(222,118,57,0.55)] transition-all hover:-translate-y-0.5 hover:bg-tangerine-deep hover:shadow-[0_12px_36px_-6px_rgba(222,118,57,0.65)]"
            >
              {CTA_LABEL}
            </Button>
            <button
              type="button"
              onClick={onExplore}
              className="h-12 rounded-full border border-cream/50 px-7 text-base font-medium text-cream transition-all hover:-translate-y-0.5 hover:border-cream hover:text-white"
            >
              Conoce el evento
            </button>
          </motion.div>

          {/* Stats del mockup */}
          <motion.dl {...stagger(5)} className="mt-12 flex flex-wrap gap-x-10 gap-y-6">
            <div>
              <dd className="text-3xl font-bold text-white sm:text-4xl">
                <Counter to={10} prefix="+" suffix="K" />
              </dd>
              <dt className="overline-label mt-1 text-cream/75">Personas en 2025</dt>
            </div>
            <div>
              <dd className="text-3xl font-bold text-white sm:text-4xl">
                <Counter to={200} prefix="+" />
              </dd>
              <dt className="overline-label mt-1 text-cream/75">Negocios locales</dt>
            </div>
            <div>
              <dd className="text-3xl font-bold text-white sm:text-4xl">$0</dd>
              <dt className="overline-label mt-1 text-cream/75">Costo para participar</dt>
            </div>
          </motion.dl>
        </div>

        {/*
          Bodegón 3D del toolkit. En escritorio es la columna derecha; en móvil
          se convierte en telón de fondo desenfocado detrás del mensaje —
          apilado bajo el texto no aporta nada y alarga la página.
        */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 aspect-square w-[135%] max-w-none -translate-x-1/2 -translate-y-1/2 select-none opacity-45 blur-[1.4vw] sm:opacity-35 lg:pointer-events-auto lg:relative lg:left-auto lg:top-auto lg:mx-auto lg:w-full lg:max-w-130 lg:translate-x-0 lg:translate-y-0 lg:opacity-100 lg:blur-none">
          <div
            aria-hidden
            className="absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(0,96,80,0.75),transparent_65%)]"
          />
          <Floating
            src={asset('img/elements/logo-3d.webp')}
            alt="Logo de ATH Móvil en 3D decorado con luces navideñas"
            className="absolute inset-[16%] z-10"
            depth={0.5}
            delay={0.3}
            drift={7}
            duration={6}
          />
          <Floating
            src={asset('img/elements/coquito.webp')}
            alt=""
            className="absolute left-[2%] top-[4%] z-20 h-[30%] w-[24%]"
            depth={1.6}
            delay={0.55}
            drift={12}
            duration={5.2}
          />
          <Floating
            src={asset('img/elements/pandereta.webp')}
            alt=""
            className="absolute right-[-2%] top-[22%] z-0 h-[26%] w-[26%]"
            depth={1.3}
            delay={0.7}
            drift={10}
            duration={5.8}
          />
          <Floating
            src={asset('img/elements/pastel.webp')}
            alt=""
            className="absolute bottom-[18%] left-[-3%] z-20 h-[22%] w-[30%]"
            depth={1.9}
            delay={0.85}
            drift={9}
            duration={5.5}
          />
          <Floating
            src={asset('img/elements/gift.webp')}
            alt=""
            className="absolute bottom-[6%] right-[4%] z-20 h-[22%] w-[26%]"
            depth={1.5}
            delay={1}
            drift={8}
            duration={6.2}
          />
          <Floating
            src={asset('img/elements/pascua.webp')}
            alt=""
            className="absolute bottom-[2%] left-[34%] z-30 h-[16%] w-[24%]"
            depth={2.2}
            delay={1.15}
            drift={6}
            duration={4.8}
          />
          <Lights />
        </div>

        {/*
          Velo sobre el telón de móvil: los objetos cálidos desenfocados bajan
          el contraste del texto si no se apagan un punto.
        */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_at_center,rgba(0,37,31,0.62),rgba(0,37,31,0.34))] lg:hidden"
        />
      </div>

      {/* Shape del toolkit cerrando el hero, como el mockup */}
      <img
        src={asset('img/logos/star-pine.webp')}
        alt=""
        aria-hidden
        className="pointer-events-none absolute -bottom-10 right-8 w-28 opacity-60 lg:w-36"
      />
    </section>
  );
}
